import { RestError } from '@azure/core-http';
import { KnownProjectModuleSettingsDtoStatus } from '@groundfloor-platform/mgmt-service-g-sdk';
import { SvgIconComponent } from '@mui/icons-material';
import { SVGIconComponent } from '../../../../ui/components/pure/icons/types';
import { castDraft } from 'immer';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { useHasUIFeatures } from '../../../../core/permissions/has-permissions.hook';
import { convertHyphenated } from '../../../../core/util/string-conversion';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import {
  mapProjectModuleToInternalDeliveryType,
  mapProjectModuleToExternalDeliveryType,
  mapProjectModuleToInboundLabel,
  mapProjectModuleToOutboundLabel
} from '../../../project-modules/mappings';
import { useQueriesGetProjectModules } from '../../../project-modules/queries';
import { useQueryGetDeliveryContainer } from '../../hubs.queries';
import { useMutationEditDeliveryContainer } from '../../lockers.queries';

export type EditDeliveryContainerProjectModulesForm = {
  selected_external_modules: string[];
  selected_internal_modules: string[];
};

export type EditDeliveryContainerProjectModulesState = {
  saving: boolean;
  loading: boolean;
  error?: RestError;
};

export type ProjectModuleAssignedToDeliveryContainer = {
  isDefaultSelected?: boolean;
  isSelected?: boolean;
  moduleStatus?: string;
  warning?: string;
  type: 'external' | 'internal';
  label: string;
  Icon?: SvgIconComponent | SVGIconComponent;
  value: string;
};

// export type ProjectModuleInheritedFromProject = {
//   status: string;
//   label: string;
//   Icon: SvgIconComponent;
//   value: string;
// };

export interface EditUserActionHandlers {
  onCancel(): void;
  onSubmit(form: EditDeliveryContainerProjectModulesState): void;
}

export interface EditDeliveryContainerProjectModulesProps {
  projectIDs: string[];
  deliveryContainerID: string;
  onAbort: () => void;
  onSuccess: () => void;
}

export function useEditDeliveryContainerProjectModulesAction({
  projectIDs,
  deliveryContainerID,
  onAbort,
  onSuccess
}: EditDeliveryContainerProjectModulesProps) {
  const hasInternalPermission = useHasUIFeatures(['internal-groundfloor']);

  const [state, setState] = useImmer<EditDeliveryContainerProjectModulesState>({ loading: false, saving: false });

  const deliveryContainer = useQueryGetDeliveryContainer(deliveryContainerID);

  const form = useForm<EditDeliveryContainerProjectModulesForm>({
    defaultValues: {
      selected_external_modules: deliveryContainer.data?.selectedExternalProjectModules || [],
      selected_internal_modules: deliveryContainer.data?.selectedInternalProjectModules || []
    }
  });

  const currentlySelectedExternalModules = useWatch({ control: form.control, name: 'selected_external_modules' });
  const currentlySelectedInternalModules = useWatch({ control: form.control, name: 'selected_internal_modules' });

  useEffect(() => {
    // Reset form values when deliveryContainer data changes
    form.reset({
      selected_external_modules: deliveryContainer.data?.selectedExternalProjectModules || [],
      selected_internal_modules: deliveryContainer.data?.selectedInternalProjectModules || []
    });
  }, [deliveryContainer.data]); // Don't include the form in the dependency array to avoid infinite loop

  const projectModules = useQueriesGetProjectModules(projectIDs);
  const modules = useMemo(() => projectModules.flatMap((result) => result.data || []), [projectModules]);

  const modulesForSelection = useMemo(() => {
    const selectedModulesExternal: ProjectModuleAssignedToDeliveryContainer[] = (
      deliveryContainer.data?.selectedExternalProjectModules || []
    ).map((module) => {
      let status: string | undefined;
      let warning: string | undefined = undefined;
      const matchingModule = modules.find((m) => m.schema.name === module);
      if (!matchingModule) {
        warning = `This Delivery Container has been selected for this Project Module, but the module does not exist on this Hub.`;
        status = 'Unknown';
      } else if (matchingModule.data.status !== KnownProjectModuleSettingsDtoStatus.Active) {
        status = matchingModule.data.status;
        warning = `This Delivery Container is selected for this Project Module, but the Project Module is not active.`;
      }

      const externalDeliveryType = mapProjectModuleToExternalDeliveryType(module);
      if (externalDeliveryType === null) {
        warning = `This Project Module does not have a valid external Delivery Type mapping.`;
      }

      return {
        isDefaultSelected: true,
        isSelected: currentlySelectedExternalModules.includes(module),
        moduleStatus: status,
        warning,
        type: 'external',
        label: mapProjectModuleToInboundLabel(module) || convertHyphenated(module, { capitalizeEachWord: true }),
        Icon: externalDeliveryType ? deliveryTypeToIconMap[externalDeliveryType] : undefined,
        value: module
      };
    });

    const selectedModulesInternal: ProjectModuleAssignedToDeliveryContainer[] = (
      deliveryContainer.data?.selectedInternalProjectModules || []
    ).map((module) => {
      let status: string | undefined;
      let warning: string | undefined = undefined;
      const matchingModule = modules.find((m) => m.schema.name === module);
      if (!matchingModule) {
        warning = `This Delivery Container has been selected for this Project Module, but the module does not exist on this Hub.`;
        status = 'Unknown';
      } else if (matchingModule.data.status !== KnownProjectModuleSettingsDtoStatus.Active) {
        status = matchingModule.data.status;
        warning = `This Delivery Container is selected for this Project Module, but the Project Module is not active.`;
      }

      const internalDeliveryType = mapProjectModuleToInternalDeliveryType(module);
      if (internalDeliveryType === null) {
        warning = `This Project Module does not have a valid internal Delivery Type mapping.`;
      }

      return {
        isDefaultSelected: true,
        isSelected: currentlySelectedInternalModules.includes(module),
        moduleStatus: status,
        warning,
        type: 'internal',
        label: mapProjectModuleToOutboundLabel(module) || convertHyphenated(module, { capitalizeEachWord: true }),
        Icon: internalDeliveryType ? deliveryTypeToIconMap[internalDeliveryType] : undefined,
        value: module
      };
    });

    const selectedModules = [...selectedModulesExternal, ...selectedModulesInternal].sort((a, b) =>
      a.label.localeCompare(b.label)
    );

    // Find any Project Modules that this Delivery Container can be selected for, but are not currently selected.
    const modulesAvailableForExternal = modules
      .filter(
        (module) =>
          !selectedModulesExternal.some((m) => m.value === module.schema.name) &&
          module.data.status === KnownProjectModuleSettingsDtoStatus.Active &&
          mapProjectModuleToExternalDeliveryType(module.schema.name) !== null
      )
      .map((module) => ({
        isDefaultSelected: false,
        isSelected: currentlySelectedExternalModules.includes(module.schema.name),
        moduleStatus: module.data.status,
        type: 'external' as const,
        label:
          mapProjectModuleToInboundLabel(module.schema.name) ||
          convertHyphenated(module.schema.name, { capitalizeEachWord: true }),
        Icon: deliveryTypeToIconMap[mapProjectModuleToExternalDeliveryType(module.schema.name)!],
        value: module.schema.name
      }));

    const modulesAvailableForInternal = modules
      .filter(
        (module) =>
          !selectedModulesInternal.some((m) => m.value === module.schema.name) &&
          module.data.status === KnownProjectModuleSettingsDtoStatus.Active &&
          mapProjectModuleToInternalDeliveryType(module.schema.name) !== null
      )
      .map((module) => ({
        isDefaultSelected: false,
        isSelected: currentlySelectedInternalModules.includes(module.schema.name),
        moduleStatus: module.data.status,
        type: 'internal' as const,
        label:
          mapProjectModuleToOutboundLabel(module.schema.name) ||
          convertHyphenated(module.schema.name, { capitalizeEachWord: true }),
        Icon: deliveryTypeToIconMap[mapProjectModuleToInternalDeliveryType(module.schema.name)!],
        value: module.schema.name
      }));

    selectedModules.push(...modulesAvailableForExternal, ...modulesAvailableForInternal);

    const visibleModules = hasInternalPermission.hasAllFeatures
      ? selectedModules
      : selectedModules.filter((m) => m.moduleStatus === KnownProjectModuleSettingsDtoStatus.Active);

    return visibleModules.sort((a, b) => {
      // Sort first by module value alphabetically, then by type (external before internal)
      if (a.value === b.value) {
        return a.type === 'external' ? -1 : 1;
      }
      return a.value.localeCompare(b.value);
    });
  }, [
    modules,
    deliveryContainer.data?.selectedExternalProjectModules,
    deliveryContainer.data?.selectedInternalProjectModules,
    hasInternalPermission.hasAllFeatures,
    currentlySelectedExternalModules,
    currentlySelectedInternalModules
  ]);

  function onChange(module: string, type: 'external' | 'internal', value: boolean) {
    setState((draft) => {
      draft.error = undefined;
    });

    const currentModules = form.getValues(`selected_${type}_modules`) || [];
    form.setValue(
      `selected_${type}_modules`,
      currentModules.filter((m) => m !== module)
    );
    if (value) {
      form.setValue(`selected_${type}_modules`, [...currentModules, module]);
    }
  }

  const mutation = useMutationEditDeliveryContainer();

  function onSubmit(form: EditDeliveryContainerProjectModulesForm) {
    setState((draft) => {
      draft.saving = true;
      draft.error = undefined;
    });

    const selectedExternalModules = form.selected_external_modules || [];
    const selectedInternalModules = form.selected_internal_modules || [];

    mutation.mutate(
      {
        deliveryContainerID,
        dto: {
          selectedExternalProjectModules: selectedExternalModules,
          selectedInternalProjectModules: selectedInternalModules
        }
      },
      {
        onSuccess() {
          onSuccess();
        },
        onError(error) {
          setState((draft) => {
            draft.saving = false;
            draft.error = castDraft(error);
          });
        }
      }
    );
  }

  return {
    form,
    state,
    handlers: {
      onCancel: onAbort,
      onSubmit,
      onChange
    },
    modulesForSelection,
    isLoading: projectModules.some((module) => module.isLoading) || state.loading
  };
}
